"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactIntl = require("react-intl");
var _reactQuery = require("react-query");
var _core = require("@folio/stripes/core");
var _smartComponents = require("@folio/stripes/smart-components");
var _components = require("@folio/stripes/components");
var _utils = require("../utils");
var _hooks = require("../hooks");
var _TableBody = _interopRequireDefault(require("./TableBody"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const SASQLookupComponent = /*#__PURE__*/(0, _react.forwardRef)((props, ref) => {
  const {
    children,
    fetchParameters = {},
    FilterComponent = () => null,
    FilterPaneHeaderComponent = () => null,
    filterPaneProps = {},
    id,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    labelOverrides = {},
    mainPaneProps,
    noSearchField,
    persistedPanesetProps = {},
    RenderBody,
    rowNavigation = true,
    // Default navigation onRowClick
    sasqProps,
    searchFieldAriaLabel,
    searchFieldProps
  } = props;
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);
  const {
    query,
    queryGetter,
    querySetter
  } = (0, _hooks.useKiwtSASQuery)();
  const {
    0: namespace
  } = (0, _core.useNamespace)();
  const ky = (0, _core.useOkapiKy)();
  const filterPaneVisibileKey = `${namespace}-${id}-filterPaneVisibility`;
  const fetchPageData = _ref => {
    let {
      pageParam = 0
    } = _ref;
    const queryMap = fetchParameters.SASQ_MAP;
    queryMap.offset = pageParam;
    return ky(`${fetchParameters.endpoint}${(0, _utils.generateKiwtQuery)(queryMap, query)}`).json();
  };
  const [filterPaneVisible, setFilterPaneVisible] = (0, _hooks.useLocalStorageState)(filterPaneVisibileKey, true);
  const toggleFilterPane = () => setFilterPaneVisible(!filterPaneVisible);
  const queryNamespace = [namespace, 'SASQ'];
  if (id) {
    queryNamespace.push(id);
  }
  queryNamespace.push('viewAll');
  queryNamespace.push(query);
  const {
    data: totalData = {},
    ...restOfInfiniteQueryProps
  } = (0, _reactQuery.useInfiniteQuery)(queryNamespace, fetchPageData);
  (0, _react.useImperativeHandle)(ref, () => ({
    lookupQueryProps: {
      data: totalData,
      ...restOfInfiniteQueryProps
    }
  }));
  const data = totalData.pages?.reduce((acc, curr) => {
    const newAcc = {
      ...acc
    };
    for (const [key, value] of Object.entries(curr)) {
      if (key !== 'page' && key !== 'result' && acc[key] !== value) {
        newAcc[key] = value;
      }
    }
    const newResults = [...(acc.results ?? [])];
    newResults.push(...(curr.results ?? []));
    newAcc.results = newResults;
    return newAcc;
  }, {}) ?? {};
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.SearchAndSortQuery, {
    initialSearchState: {
      query: ''
    },
    queryGetter: queryGetter,
    querySetter: querySetter,
    ...sasqProps,
    children: sasqRenderProps => {
      const {
        activeFilters,
        filterChanged,
        getFilterHandlers,
        getSearchHandlers,
        onSubmitSearch,
        resetAll,
        searchChanged,
        searchValue
      } = sasqRenderProps;
      const searchHandlers = getSearchHandlers();
      const disableReset = !filterChanged && !searchChanged;
      const filterCount = activeFilters.string ? activeFilters.string.split(',').length : 0;
      const Body = RenderBody ?? _TableBody.default;
      const {
        filterPaneFirstMenu,
        filterPaneLastMenu,
        ...restOfFilterPaneProps
      } = filterPaneProps;
      const {
        mainPaneFirstMenu,
        mainPaneLastMenu,
        ...restOfMainPaneProps
      } = mainPaneProps;
      const internalStateProps = {
        activeFilters,
        filterCount,
        filterPaneVisible,
        searchChanged,
        searchValue,
        setFilterPaneVisible,
        toggleFilterPane
      };
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_smartComponents.PersistedPaneset, {
        appId: namespace,
        id: `${id}-paneset`,
        ...persistedPanesetProps,
        children: [filterPaneVisible && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
          defaultWidth: "20%",
          firstMenu: filterPaneFirstMenu ? filterPaneFirstMenu(internalStateProps) : null,
          id: `${id}-filter-pane`,
          lastMenu: filterPaneLastMenu ? filterPaneLastMenu(internalStateProps) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.PaneMenu, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.CollapseFilterPaneButton, {
              onClick: toggleFilterPane
            })
          }),
          paneTitle: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
            id: "stripes-smart-components.searchAndFilter"
          }),
          ...restOfFilterPaneProps,
          children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("form", {
            onSubmit: onSubmitSearch,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(FilterPaneHeaderComponent, {}), !noSearchField && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
              children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.SearchField, {
                ariaLabel: searchFieldAriaLabel,
                autoFocus: true,
                id: `sasq-search-field-${id}`,
                name: "query",
                onChange: e => {
                  if (e.target?.value) {
                    searchHandlers.query(e); // SASQ needs the whole event here
                  } else {
                    searchHandlers.reset();
                  }
                },
                onClear: searchHandlers.reset,
                value: searchValue.query,
                ...searchFieldProps
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
                buttonStyle: "primary",
                disabled: !searchValue.query,
                fullWidth: true,
                type: "submit",
                children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
                  id: "stripes-smart-components.search"
                })
              }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
                buttonStyle: "none",
                disabled: disableReset,
                id: "clickable-reset-all",
                onClick: resetAll,
                children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Icon, {
                  icon: "times-circle-solid",
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactIntl.FormattedMessage, {
                    id: "stripes-smart-components.resetAll"
                  })
                })
              })]
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)(FilterComponent, {
              activeFilters: activeFilters.state,
              filterChanged: filterChanged,
              filterHandlers: getFilterHandlers(),
              resetAll: resetAll,
              searchChanged: searchChanged,
              searchHandlers: getSearchHandlers(),
              searchValue: searchValue
            })]
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
          defaultWidth: "fill",
          firstMenu: mainPaneFirstMenu ? mainPaneFirstMenu(internalStateProps) : !filterPaneVisible ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.PaneMenu, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_smartComponents.ExpandFilterPaneButton, {
              filterCount: filterCount,
              onClick: toggleFilterPane
            })
          }) : null,
          id: `${id}-main-pane`,
          lastMenu: mainPaneLastMenu ? mainPaneLastMenu(internalStateProps) : null,
          noOverflow: true,
          padContent: false,
          paneSub: kintIntl.formatKintMessage({
            id: 'found#Values',
            overrideValue: labelOverrides?.foundValues
          }, {
            total: data?.total
          }),
          ...restOfMainPaneProps,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(Body, {
            data: data,
            filterPaneVisible: filterPaneVisible,
            intlKey: passedIntlKey,
            intlNS: passedIntlNS,
            labelOverrides: labelOverrides,
            query: query,
            rowNavigation: rowNavigation,
            toggleFilterPane: toggleFilterPane,
            ...restOfInfiniteQueryProps,
            ...sasqRenderProps,
            ...sasqProps,
            ...props
          })
        }), children]
      });
    }
  });
});
SASQLookupComponent.propTypes = {
  children: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  fetchParameters: _propTypes.default.object,
  filterPaneProps: _propTypes.default.object,
  FilterComponent: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  FilterPaneHeaderComponent: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  history: _propTypes.default.object,
  id: _propTypes.default.string.isRequired,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  labelOverrides: _propTypes.default.object,
  location: _propTypes.default.object,
  mainPaneProps: _propTypes.default.object,
  match: _propTypes.default.object,
  mclProps: _propTypes.default.object,
  noSearchField: _propTypes.default.bool,
  path: _propTypes.default.string.isRequired,
  persistedPanesetProps: _propTypes.default.object,
  RenderBody: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),
  resource: _propTypes.default.object,
  resultColumns: _propTypes.default.arrayOf(_propTypes.default.object),
  rowNavigation: _propTypes.default.bool,
  sasqProps: _propTypes.default.object,
  searchFieldAriaLabel: _propTypes.default.string,
  searchFieldProps: _propTypes.default.object
};
var _default = exports.default = SASQLookupComponent;